import { DraggableComponentTypes } from "../types/draggable";
import { v4 as uuidv4 } from "uuid";
import { imagePlaceholder } from "./placeholders";
import classNames from "./classNames";
import { twMerge } from "tailwind-merge";
const convertAttributeToProp = (attribute, value) => {
    switch (attribute) {
        case "class":
            return {
                propName: "className",
                value: value.replace(/dark:[^\s]+/g, '').replace("indigo", 'site').replace('primary', 'site'),
            };
        case "style":
            return {
                propName: "style",
                value: null
            };
        case "contenteditable":
            return {};
        default:
            return {
                propName: attribute,
                value,
            };
    }
};
const hasHTML = (htmlString) => {
    var htmlRegex = /<[^>]*>/;
    return htmlRegex.test(htmlString);
};
const getComponentType = (tagName) => {
    const tagNameMapping = {
        div: DraggableComponentTypes.Div,
        span: DraggableComponentTypes.Div,
        section: DraggableComponentTypes.Div,
        h1: DraggableComponentTypes.Title,
        h2: DraggableComponentTypes.Title,
        h3: DraggableComponentTypes.Title,
        h4: DraggableComponentTypes.Title,
        h5: DraggableComponentTypes.Title,
        h6: DraggableComponentTypes.Title,
        a: DraggableComponentTypes.Link,
        p: DraggableComponentTypes.Text,
        img: DraggableComponentTypes.Image,
        form: DraggableComponentTypes.Form,
        dl: DraggableComponentTypes.Div,
        dt: DraggableComponentTypes.Div,
        dd: DraggableComponentTypes.Div,
        svg: DraggableComponentTypes.Svg,
        ul: DraggableComponentTypes.List,
        li: DraggableComponentTypes.ListItem,
        strong: DraggableComponentTypes.Text,
        button: DraggableComponentTypes.Button,
        footer: DraggableComponentTypes.Div,
        header: DraggableComponentTypes.Div,
        article: DraggableComponentTypes.Div,
    };
    return tagNameMapping[tagName.toLowerCase()];
};
const cleanUpClassName = (className) => {
    return twMerge(className.replace(/ {2,}/g, ' '));
};
const parseHtmlAttributes = (attributes, obj) => {
    if (attributes.length > 0) {
        [...attributes].forEach((attribute) => {
            const convertedProp = convertAttributeToProp(attribute.name, attribute.value);
            obj.props[convertedProp.propName] = convertedProp.value;
        });
        if (obj.props.className.includes("grid")) {
            obj.type = DraggableComponentTypes.Grid;
            obj.props = {
                ...obj.props,
            };
        }
        if (obj.type === DraggableComponentTypes.Image) {
            obj.props.image = {
                src: obj.props?.src.includes("http") ? obj.props?.src : "https://placehold.co/600x400?text=Placeholder",
                alt: obj.props?.alt,
                file: imagePlaceholder.file,
            };
        }
    }
};
const parseTextContent = (element, obj) => {
    if (!hasHTML(element.innerHTML) && element.textContent.trim() !== "" && element.textContent.trim() !== "\n") {
        obj.props.text = element.textContent.trim();
    }
    if (hasHTML(element.innerHTML) && ['p'].includes(element.tagName.toLowerCase())) {
        obj.props.text = element.textContent.trim();
    }
    if (element.innerText && obj.type !== DraggableComponentTypes.Div) {
        if ([DraggableComponentTypes.Text].includes(obj.type)) {
            if (obj.props.className.length > 0) {
                obj.props.className += " ";
            }
            obj.props.className += " prose max-w-none";
            obj.props.className = classNames(obj.props.className);
        }
    }
};
const parseChildNodes = (element, obj) => {
    const childNodes = element.childNodes;
    if (childNodes.length > 0) {
        obj.children = [];
        [...childNodes].forEach((childNode) => {
            if (childNode.nodeType === Node.TEXT_NODE) {
                if ([DraggableComponentTypes.Link, DraggableComponentTypes.Title, DraggableComponentTypes.ListItem].includes(obj.type)) {
                    if (childNode.nodeValue.trim() !== "" && hasHTML(childNode.nodeValue)) {
                        obj.children.push({
                            type: "div",
                            id: `${obj.id}/${uuidv4()}`,
                            props: {
                                component: "span",
                                text: childNode.nodeValue.trim(),
                            },
                        });
                    }
                    else {
                        if (obj.type === DraggableComponentTypes.Link) {
                            obj.props.link = { label: childNode.nodeValue };
                        }
                        else {
                            if (DraggableComponentTypes.ListItem === obj.type && childNode.nodeValue.trim() !== "") {
                                obj.children.push({
                                    type: "div",
                                    id: `${obj.id}/${uuidv4()}`,
                                    props: {
                                        component: "span",
                                        text: childNode.nodeValue.trim(),
                                    },
                                });
                            }
                            obj.props.text = childNode.nodeValue.replace(/(<([^>]+)>)/gi, "").trim();
                        }
                    }
                }
                else {
                    if (childNode.nodeValue.trim().length > 2) {
                        obj.props.text = childNode.nodeValue.replace(/(<([^>]+)>)/gi, "").trim();
                    }
                }
            }
            else if (childNode.nodeType === Node.ELEMENT_NODE) {
                const child = htmlToJson(childNode, obj);
                if (child) {
                    obj.children.push(child);
                }
            }
        });
    }
};
function htmlToJson(element, parent = null) {
    const obj = {};
    const tagName = element.tagName?.toLowerCase();
    const componentType = getComponentType(tagName);
    if (!componentType) {
        return;
    }
    obj.type = componentType;
    if (parent && parent.type === DraggableComponentTypes.Grid && componentType === DraggableComponentTypes.Div) {
        obj.type = DraggableComponentTypes.GridItem;
    }
    if (parent && parent.type === DraggableComponentTypes.List) {
        obj.type = DraggableComponentTypes.ListItem;
    }
    obj.id = parent ? `${parent.id}/${uuidv4()}` : "1";
    obj.props = { className: " " };
    if ([DraggableComponentTypes.Div, DraggableComponentTypes.Title].includes(componentType) && tagName !== "article") {
        obj.props.component = tagName;
    }
    parseHtmlAttributes(element.attributes, obj);
    parseTextContent(element, obj);
    if (obj.type === DraggableComponentTypes.Svg) {
        obj.props.svgContent = element.innerHTML;
    }
    obj.props.className = cleanUpClassName(obj.props.className);
    parseChildNodes(element, obj);
    return obj;
}
const htmlToDraggable = (html, parentId = 1) => {
    let element = document.createElement("div");
    element.innerHTML = html;
    return {
        id: 1,
        children: [htmlToJson(element.childNodes[0], { id: parentId })],
    };
};
export default htmlToDraggable;
//# sourceMappingURL=htmlToDraggable.js.map